/* eslint-disable react/no-unstable-nested-components */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "components/ui/classNames";
import TablePagination from "components/ui/Table/TablePagination";
import differenceInYears from "date-fns/differenceInYears";
import getYear from "date-fns/getYear";
import isValid from "date-fns/isValid";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface BrazilianDividendsPaidProps {
  dividendsPaid: IDividendsPaidDetails[];
  isRenderPageDefault: boolean;
}

export default function BrazilianDividendsPaid({ dividendsPaid, isRenderPageDefault }: BrazilianDividendsPaidProps) {
  const columnHelper = createColumnHelper<IDividendsPaidDetails>();
  const intl = useIntl();

  const columns = [
    columnHelper.accessor("dividendType", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Tipo" id="wt+XcA" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span
            className={classNames(
              "whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-400",
              {
                "bg-sky-600/10 text-sky-800": info.getValue() === "JUROS",
                "bg-yellow-600/10 text-yellow-800": info.getValue() !== "JUROS",
              },
            )}
          >
            {info.getValue() === "JUROS" ? "JCP" : <FormattedMessage defaultMessage="Dividendo" id="/E+MJo" />}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("recordDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data-Com" id="uJFyej" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>
            {intl.formatDate(parseDateYmd(info.getValue()), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("excludingDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data-Ex" id="tsg1zP" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>
            {getExcludingDate(intl, parseDateYmd(info.row.original.recordDate), parseDateYmd(info.getValue()))}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("payDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Pagamento" id="23+rmI" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{info.getValue() ? getPayDate(parseDateYmd(info.getValue()), intl) : "-"}</span>
        </div>
      ),
    }),
    columnHelper.accessor("amountPaid", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Valor (R$)" description="Valor dos dividendos pagos em reais" id="XydT1i" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{numeral(info.getValue()).format("0.00").replace(".", ",")}</span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: dividendsPaid,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: isRenderPageDefault ? 8 : 16,
      },
    },
  });

  return (
    <div className="flex h-full w-full flex-col">
      <div className="pb-4 lg:w-full">
        <div className={classNames("relative overflow-auto rounded-xl border border-slate-200 dark:border-slate-950")}>
          <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                >
                  {headerGroup.headers
                    .filter((header) => !header.column.columnDef.meta?.isHidden)
                    .map((header) => {
                      return (
                        <th
                          key={header.id}
                          className={classNames(
                            "m-0 bg-slate-50 p-0 text-center",
                            header.column.columnDef.meta?.header?.className,
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      );
                    })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                    {row
                      .getVisibleCells()
                      .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                      .map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <TablePagination<IDividendsPaidDetails> table={table} />
      </div>
    </div>
  );
}

export function getExcludingDate(intl: IntlShape, recordDate: Date, excludingDate: Date) {
  const difference = differenceInYears(excludingDate, recordDate);

  if (difference >= 2) {
    return "-";
  }

  return intl.formatDate(excludingDate, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

export function getPayDate(payDate: Date, intl: IntlShape) {
  if (getYear(payDate) === 1900 || !isValid(payDate)) {
    return "-";
  }

  if (getYear(payDate) >= 2500) {
    return intl.formatMessage({
      defaultMessage: "Sem data definida",
      id: "vf+lDR",
      description: "Mensagem exibida quando a data de pagamento de dividendos não está definida",
    });
  }

  return intl.formatDate(payDate, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}
